import {useState} from 'react'
import {Link} from 'react-router-dom'
import {useTranslation} from 'react-i18next'
import {Input} from '@/components/ui/input'
import {Skeleton} from '@/components/ui/skeleton'
import {CiiuPicker, RiskOptionsPicker} from '@/components/ciiu-risk-picker'
import {Reveal} from '@/components/reveal'
import {useRiskOptions, type CiiuCode} from '@/hooks/common/referencias/use-ciiu'
import {usePrecioResuelto} from '@/hooks/inci/cartera/use-public-precios'
import {formatCopFull} from '@/lib/money'
import {cn} from '@/lib/utils'
import {CTA_LINEA, CTA_SELLO, SeccionHead} from './kit'

/** `mt-auto` alinea los CTA de las tres tarjetas aunque lo de arriba tenga largos distintos. */
function PieDeTarjeta({href, testId, linea}: {href: string; testId: string; linea?: boolean}) {
  const {t} = useTranslation()
  return (
    <div className="mt-auto pt-6">
      <Link to={href} data-testid={testId} className={cn(linea ? CTA_LINEA : CTA_SELLO, 'w-full')}>
        {t('landing.precios.cta')}
      </Link>
      <p className="mt-2 text-center text-[11px] text-muted-foreground">{t('landing.precios.ctaAyuda')}</p>
    </div>
  )
}

/** `cop` ausente = sin precio vigente para esa banda: se dice, sin $0 ni esconder la tarjeta. */
function TarjetaPrecio({
  ciclo,
  cop,
  destacada,
  ctaHref,
}: {
  ciclo: 'mensual' | 'anual'
  cop: number | undefined
  destacada?: boolean
  ctaHref: string
}) {
  const {t} = useTranslation()
  return (
    <div
      data-testid={`landing-precio-${ciclo}`}
      className={cn('surface flex flex-col p-6', destacada && 'border-primary ring-1 ring-primary/20')}>
      <p className="font-mono text-[10px] font-semibold tracking-[0.12em] text-muted-foreground uppercase">
        {t(`landing.precios.${ciclo}`)}
      </p>
      <p className="mt-2.5 font-mono text-[26px] leading-none font-medium tabular-nums text-foreground">
        {cop == null ? (
          <span className="text-base font-normal text-muted-foreground">{t('landing.precios.sinPrecio')}</span>
        ) : (
          formatCopFull(cop)
        )}
      </p>
      <p className="mt-2 text-xs text-muted-foreground">{t('landing.precios.antesDeIva')}</p>
      <PieDeTarjeta href={ctaHref} testId={`landing-precio-${ciclo}-cta`} />
    </div>
  )
}

function TarjetaConProfesional({ctaHref}: {ctaHref: string}) {
  const {t} = useTranslation()
  return (
    <div className="surface flex flex-col bg-secondary/60 p-6" data-testid="landing-precio-profesional">
      <p className="font-mono text-[10px] font-semibold tracking-[0.12em] text-primary uppercase">
        {t('landing.precios.profesional.eyebrow')}
      </p>
      <p className="mt-2.5 font-heading text-lg text-foreground">{t('landing.precios.profesional.title')}</p>
      <p className="mt-2 text-sm text-muted-foreground">{t('landing.precios.profesional.body')}</p>
      <p className="mt-3 font-mono text-xs text-foreground">{t('landing.precios.profesional.precio')}</p>
      <PieDeTarjeta href={ctaHref} testId="landing-precio-profesional-cta" linea />
    </div>
  )
}

export function PreciosResolver() {
  const {t} = useTranslation()
  const [ciiu, setCiiu] = useState<CiiuCode | null>(null)
  const [riskId, setRiskId] = useState('')
  const [empleados, setEmpleados] = useState('')

  const {data: opcionesRiesgo} = useRiskOptions(ciiu?.code ?? null)
  const claseRiesgo = opcionesRiesgo?.find((o) => o.id === riskId)?.risk_class ?? null
  const nEmpleados = /^\d+$/.test(empleados) && Number(empleados) >= 1 ? Number(empleados) : null

  const {data: resuelto, isFetching, isError} = usePrecioResuelto(nEmpleados, claseRiesgo)
  const montoDe = (ciclo: 'mensual' | 'anual') => resuelto?.opciones.find((o) => o.billing_cycle === ciclo)?.amount_cop

  // `registro-empresa` lee estos parámetros y precarga el paso 1.
  const ctaHref =
    ciiu && riskId && nEmpleados
      ? `/registro/empresa?ciiu=${encodeURIComponent(ciiu.code)}&riesgo=${encodeURIComponent(riskId)}&empleados=${nEmpleados}`
      : '/registro/empresa'

  return (
    <section className="bg-background" data-testid="landing-precios">
      <div className="mx-auto max-w-6xl px-6 py-20 sm:py-28">
        <SeccionHead
          eyebrow={t('landing.precios.eyebrow')}
          title={t('landing.precios.title')}
          body={t('landing.precios.subtitle')}
        />

        <Reveal delayMs={60} className="surface mt-12 max-w-2xl p-6">
          <div className="grid gap-5 sm:grid-cols-2">
            <div className="min-w-0 sm:col-span-2">
              <label className="font-mono text-[10px] font-semibold tracking-wider text-muted-foreground uppercase">
                {t('landing.precios.form.actividad')}
              </label>
              <div className="mt-2">
                <CiiuPicker
                  ciiu={ciiu}
                  onChange={(next) => {
                    setCiiu(next)
                    setRiskId('')
                  }}
                />
              </div>
            </div>
            {ciiu && (
              <div className="min-w-0 sm:col-span-2">
                <label className="font-mono text-[10px] font-semibold tracking-wider text-muted-foreground uppercase">
                  {t('landing.precios.form.riesgo')}
                </label>
                <p className="mt-1 mb-2 text-xs text-muted-foreground">{t('landing.precios.form.riesgoAyuda')}</p>
                <RiskOptionsPicker ciiuCode={ciiu.code} riskId={riskId} onPick={setRiskId} />
              </div>
            )}
            <div className="min-w-0">
              <label
                htmlFor="landing-precios-empleados"
                className="font-mono text-[10px] font-semibold tracking-wider text-muted-foreground uppercase">
                {t('landing.precios.form.empleados')}
              </label>
              <Input
                id="landing-precios-empleados"
                data-testid="landing-precios-empleados"
                type="number"
                min={1}
                inputMode="numeric"
                className="mt-2"
                value={empleados}
                onChange={(e) => setEmpleados(e.target.value)}
              />
            </div>
          </div>
        </Reveal>

        {isError ? (
          <div className="surface mt-8 max-w-2xl p-6" data-testid="landing-precios-error">
            <p className="text-sm text-foreground">{t('landing.precios.errorTitulo')}</p>
            <p className="mt-1 text-xs text-muted-foreground">{t('landing.precios.errorAyuda')}</p>
            <Link to={ctaHref} data-testid="landing-precios-error-cta" className={cn(CTA_SELLO, 'mt-5')}>
              {t('landing.precios.cta')}
            </Link>
          </div>
        ) : resuelto && !isFetching ? (
          <Reveal delayMs={90} className="mt-8 grid items-stretch gap-4 sm:grid-cols-3">
            <TarjetaPrecio ciclo="mensual" cop={montoDe('mensual')} ctaHref={ctaHref} />
            <TarjetaPrecio ciclo="anual" cop={montoDe('anual')} destacada ctaHref={ctaHref} />
            <TarjetaConProfesional ctaHref={ctaHref} />
          </Reveal>
        ) : isFetching ? (
          <Skeleton className="mt-8 h-56 w-full rounded-xl" data-testid="landing-precios-skeleton" />
        ) : null}

        <p className="mt-6 text-xs text-muted-foreground">{t('landing.precios.footnote')}</p>
      </div>
    </section>
  )
}
